import React, { useEffect, useState } from 'react';
import MatchConduct from './MatchConduct';
import './MatchConductModal.scss';

interface MatchConductModalProps {
  isOpen: boolean;
  matchId: number | null;
  onClose: () => void;
  onFinished?: () => void;
}

const MatchConductModal: React.FC<MatchConductModalProps> = ({ isOpen, matchId, onClose, onFinished }) => {
  const [isStarted, setIsStarted] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [winnerBanner, setWinnerBanner] = useState<string | null>(null);

  // Сбрасываем локальное состояние, когда попап закрывается/открывается заново
  useEffect(() => {
    if (!isOpen) {
      setIsStarted(false);
      setShowCancelConfirm(false);
      setWinnerBanner(null);
    }
  }, [isOpen]);

  // Жёсткая блокировка ESC, пока поединок идёт
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isStarted && !winnerBanner) {
        e.preventDefault();
        e.stopPropagation();
        setShowCancelConfirm(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isStarted, winnerBanner]);

  if (!isOpen || matchId == null) return null;

  const isLocked = isStarted && !winnerBanner;

  const handleOverlayClick = () => {
    if (!isLocked) {
      onClose();
    } else {
      setShowCancelConfirm(true);
    }
  };

  const handleCloseClick = () => {
    if (!isLocked) {
      onClose();
    } else {
      setShowCancelConfirm(true);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleFinished = (winnerName: string) => {
    setWinnerBanner(winnerName);
    onFinished?.();
    // Задержка, чтобы пользователь увидел сообщение «Победил X», прежде чем попап закроется
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <div className="match-conduct-modal-overlay" onClick={handleOverlayClick}>
      <div className="match-conduct-modal" onClick={e => e.stopPropagation()}>
        <div className="match-conduct-modal__header">
          <h2>Проведение поединка</h2>
          {!isLocked && (
            <button className="match-conduct-modal__close" onClick={handleCloseClick} aria-label="Закрыть">×</button>
          )}
        </div>

        <div className="match-conduct-modal__body">
          {winnerBanner ? (
            <div className="match-conduct-modal__winner">
              <h3>Победил {winnerBanner}</h3>
              <p>Окно закроется автоматически…</p>
            </div>
          ) : (
            <MatchConduct
              matchId={matchId}
              onStartedChange={setIsStarted}
              onFinished={handleFinished}
            />
          )}
        </div>

        {isLocked && (
          <div className="match-conduct-modal__footer">
            <span className="match-conduct-modal__lock-hint">
              Поединок идёт — закрыть нельзя
            </span>
            <button
              className="match-conduct-modal__cancel-btn"
              onClick={() => setShowCancelConfirm(true)}
            >
              Отменить поединок
            </button>
          </div>
        )}
      </div>

      {showCancelConfirm && (
        <div
          className="match-conduct-modal__confirm-overlay"
          onClick={e => e.stopPropagation()}
        >
          <div className="match-conduct-modal__confirm">
            <h3>Точно отменить поединок?</h3>
            <p>
              Локально набранные очки будут потеряны. Поединок останется в статусе «в процессе»
              и его можно будет открыть и продолжить позже.
            </p>
            <div className="match-conduct-modal__confirm-actions">
              <button
                className="match-conduct-modal__btn match-conduct-modal__btn--secondary"
                onClick={() => setShowCancelConfirm(false)}
              >
                Нет, продолжить поединок
              </button>
              <button
                className="match-conduct-modal__btn match-conduct-modal__btn--danger"
                onClick={handleConfirmCancel}
              >
                Да, отменить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchConductModal;
